import React from "react"
import {
	Box, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { AppBulkEditAction, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppValueChip } from "@components"
import { dsColorNew } from "@palette"
import { autoEuropeSeasonSetsMap } from "Pages/AutoEuropeTool/Page/@signals"

const UNDERCUT_TYPE_LABEL = { 0: '€', 1: '%' }
const UNDERCUT_DIRECTION_LABEL = { 0: '▼ Down', 1: '▲ Up' }

// ─── item formatters ───────────────────────────────────────────────────────────

const fmtProvider = p => {
	const dur = p.duration ? ` (${p.duration.durationFrom}–${p.duration.durationTo}d)` : ''
	const dir = p.direction !== undefined ? ` ${UNDERCUT_DIRECTION_LABEL[p.direction] ?? ''}` : ''
	return `${p.providerName || `#${p.providerId}`}: ${p.value}${p.type !== undefined ? ` ${UNDERCUT_TYPE_LABEL[p.type] ?? ''}` : ''}${dir}${dur}`
}

const fmtSeason = s => `${s.seasonFrom} → ${s.seasonTo}`

const fmtDuration = d => `${d.durationFrom} → ${d.durationTo}`

const fmtFilterItem = item => (typeof item === 'object' && item !== null) ? item.label : String(item)

// ─── diff helpers ──────────────────────────────────────────────────────────────

const serialize = v => JSON.stringify(v ?? null)

const resolveSeasonSetName = v => {
	if (!v) return null
	const id = typeof v === 'object' ? (v.value ?? v) : v
	return autoEuropeSeasonSetsMap.value[id] || id
}

function scalarDiff(label, oldVal, newVal) {
	if (serialize(oldVal) === serialize(newVal)) return null
	return { label, type: 'scalar', oldVal: String(oldVal ?? '-'), newVal: String(newVal ?? '-') }
}

function arrayDiff(label, oldArr, newArr, keyFn, labelFn) {
	const old_ = oldArr ?? []
	const new_ = newArr ?? []
	if (serialize(old_) === serialize(new_)) return null

	const oldKeys = old_.map(keyFn)
	const newKeys = new_.map(keyFn)

	const removed = old_.filter(i => !newKeys.includes(keyFn(i))).map(labelFn)
	const added = new_.filter(i => !oldKeys.includes(keyFn(i))).map(labelFn)
	const changed = new_.filter(i => {
		if (!oldKeys.includes(keyFn(i))) return false
		const orig = old_.find(o => keyFn(o) === keyFn(i))
		return serialize(orig) !== serialize(i)
	}).map(i => {
		const orig = old_.find(o => keyFn(o) === keyFn(i))
		return { before: labelFn(orig), after: labelFn(i) }
	})

	return { label, type: 'array', added, removed, changed }
}

function buildDiff(original, current) {
	const rows = []
	const push = d => { if (d) rows.push(d) }

	push(scalarDiff('Name', original.name, current.name))
	push(scalarDiff('Season Set', resolveSeasonSetName(original.seasonSetId), resolveSeasonSetName(current.seasonSetId)))
	push(scalarDiff('Min. Margin value', original.minimumMargin?.value, current.minimumMargin?.value))
	push(arrayDiff('Min. Margin overrides',
		original.minimumMargin?.providers, current.minimumMargin?.providers,
		p => p.providerId, fmtProvider
	))
	push(scalarDiff('Min. Profit value', original.minimumProfit?.value, current.minimumProfit?.value))
	push(arrayDiff('Min. Profit overrides',
		original.minimumProfit?.providers, current.minimumProfit?.providers,
		p => p.providerId, fmtProvider
	))
	push(scalarDiff('Undercut value', original.undercut?.value, current.undercut?.value))
	push(scalarDiff('Undercut type', UNDERCUT_TYPE_LABEL[original.undercut?.type], UNDERCUT_TYPE_LABEL[current.undercut?.type]))
	push(scalarDiff('Undercut direction', UNDERCUT_DIRECTION_LABEL[original.undercut?.direction ?? 0], UNDERCUT_DIRECTION_LABEL[current.undercut?.direction ?? 0]))
	push(arrayDiff('Undercut overrides',
		original.undercut?.providers, current.undercut?.providers,
		p => p.providerId, fmtProvider
	))
	push(arrayDiff('Seasons',
		original.seasons, current.seasons,
		s => `${s.seasonFrom}|${s.seasonTo}`, fmtSeason
	))
	push(arrayDiff('Durations',
		original.durations, current.durations,
		d => `${d.durationFrom}|${d.durationTo}`, fmtDuration
	))
	push(scalarDiff('Agency ID', original.agencyId, current.agencyId))
	push(scalarDiff('Agent ID', original.agentId, current.agentId))
	push(arrayDiff('Reports',
		original.reports, current.reports,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(arrayDiff('Offices filter',
		original.filterGroupOffices, current.filterGroupOffices,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Offices filter mode',
		original.filterGroupOfficesExclusion ? 'Exclude' : 'Include',
		current.filterGroupOfficesExclusion ? 'Exclude' : 'Include'
	))
	push(arrayDiff('Sites filter',
		original.filterGroupSites, current.filterGroupSites,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Sites filter mode',
		original.filterGroupSitesExclusion ? 'Exclude' : 'Include',
		current.filterGroupSitesExclusion ? 'Exclude' : 'Include'
	))
	push(arrayDiff('Providers filter',
		original.filterGroupProviders, current.filterGroupProviders,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Providers filter mode',
		original.filterGroupProvidersExclusion ? 'Exclude' : 'Include',
		current.filterGroupProvidersExclusion ? 'Exclude' : 'Include'
	))
	push(arrayDiff('Web Supplier Groups filter',
		original.filterGroupWebSuppliers, current.filterGroupWebSuppliers,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Web Supplier Groups filter mode',
		original.filterGroupWebSuppliersExclusion ? 'Exclude' : 'Include',
		current.filterGroupWebSuppliersExclusion ? 'Exclude' : 'Include'
	))
	push(arrayDiff('Acriss filter',
		original.filterAcriss, current.filterAcriss,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Acriss filter mode',
		original.filterAcrissExclusion ? 'Exclude' : 'Include',
		current.filterAcrissExclusion ? 'Exclude' : 'Include'
	))
	push(arrayDiff('Categories filter',
		original.filterCategories, current.filterCategories,
		item => (typeof item === 'object' && item !== null) ? item.value : item, fmtFilterItem
	))
	push(scalarDiff('Categories filter mode',
		original.filterCategoriesExclusion ? 'Exclude' : 'Include',
		current.filterCategoriesExclusion ? 'Exclude' : 'Include'
	))
	push(scalarDiff('Compare By',
		original.compareByCategory ? 'Category' : 'ACRISS',
		current.compareByCategory ? 'Category' : 'ACRISS'
	))
	push(scalarDiff('Target Position', original.targetPosition ?? '-', current.targetPosition ?? '-'))
	push(scalarDiff('Insurance delta', original.insurance?.delta ?? '-', current.insurance?.delta ?? '-'))
	push(arrayDiff('Insurance delta ranges',
		original.insurance?.deltaByDuration, current.insurance?.deltaByDuration,
		x => `${x.durationFrom}|${x.durationTo}`,
		x => `${x.durationFrom}–${x.durationTo}d · ${x.delta}/day`
	))
	push(arrayDiff('Insurance profit/day ranges',
		original.insurance?.profitPerDay, current.insurance?.profitPerDay,
		x => `${x.durationFrom}|${x.durationTo}`,
		x => `${x.durationFrom}–${x.durationTo}d · ${x.min}–${x.max}/day`
	))

	return rows
}

// ─── sub-components ────────────────────────────────────────────────────────────

function ScalarRow({ label, oldVal, newVal }) {
	return (
		<TableRow>
			<TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>{label}</TableCell>
			<TableCell sx={{ verticalAlign: 'top' }}>
				<AppValueChip sx={{ maxWidth: 160, whiteSpace: 'normal' }}>{oldVal}</AppValueChip>
			</TableCell>
			<TableCell sx={{ verticalAlign: 'top' }}>
				<AppValueChip tone="accent" sx={{ maxWidth: 160, whiteSpace: 'normal' }}>{newVal}</AppValueChip>
			</TableCell>
		</TableRow>
	)
}

function ArrayRow({ label, added, removed, changed }) {
	return (
		<TableRow>
			<TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>{label}</TableCell>
			<TableCell colSpan={2}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
					{removed.map((item, i) => (
						<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<IconSeeker icon="minus" fontSize="small" sx={{ color: dsColorNew.dangerRed, fontSize: 12 }}/>
							<Typography sx={{ fontSize: 12, color: dsColorNew.dangerRed }}>{item}</Typography>
						</Box>
					))}
					{added.map((item, i) => (
						<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<IconSeeker icon="plus" fontSize="small" sx={{ color: dsColorNew.successText, fontSize: 12 }}/>
							<Typography sx={{ fontSize: 12, color: dsColorNew.successText }}>{item}</Typography>
						</Box>
					))}
					{changed.map((item, i) => (
						<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
							<IconSeeker icon="pen" fontSize="small" sx={{ color: dsColorNew.noteAmber, fontSize: 12 }}/>
							<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, textDecoration: 'line-through' }}>{item.before}</Typography>
							<IconSeeker icon="arrow-right" fontSize="small" sx={{ fontSize: 10 }}/>
							<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber }}>{item.after}</Typography>
						</Box>
					))}
				</Box>
			</TableCell>
		</TableRow>
	)
}

// ─── main component ────────────────────────────────────────────────────────────

export default function ConfirmEditModal({ open, onClose, onConfirm, original, current, isLoading }) {
	if (!original || !current) return null

	const diff = buildDiff(original, current)

	return (
		<AppDialog open={open} onClose={onClose} maxWidth="sm">
			<AppDialogHeader icon="floppy-disk" title="Confirm changes" onClose={onClose}/>
			<AppDialogBody>
				{diff.length === 0 ? (
					<Typography sx={{ fontSize: 14, color: dsColorNew.textMuted }}>No changes detected.</Typography>
				) : (
					<>
						<Typography sx={{ mb: 1.5, fontSize: 14, color: dsColorNew.textMuted }}>
							The following fields will be updated:
						</Typography>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ width: '30%' }}>Field</TableCell>
									<TableCell>Before</TableCell>
									<TableCell>After</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{diff.map(row => row.type === 'scalar'
									? <ScalarRow key={row.label} {...row}/>
									: <ArrayRow key={row.label} {...row}/>
								)}
							</TableBody>
						</Table>
					</>
				)}
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}/>
				<AppBulkEditAction
					loading={isLoading}
					startIcon={<IconSeeker icon="floppy-disk"/>}
					onClick={onConfirm}
					disabled={diff.length === 0}
				>
					Confirm
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	)
}